import { useCallback, useState } from 'react';
import { useLocalStorage } from '@hooks/useLocalStorage';
import type { CreateSessionResponse, ResumeSessionResponse } from '@t/session.types';
import {
  CPQ_SESSION_ID_KEY,
  CPQ_RESUME_TOKEN_KEY,
  CPQ_SESSION_STORED_AT_KEY,
  CPQ_QUALIFICATION_CACHE_KEY,
  CPQ_QUALIFICATION_FLOW_KEY,
  CPQ_CHECKOUT_STATE_KEY,
  CPQ_ORDER_DATA_KEY,
  SESSION_TTL_MS,
} from '@t/session.types';

export interface SessionStorageState {
  storedSessionId: string | null;
  storedResumeToken: string | null;
  currentStep: string | null;
  expiresAt: string | null;
  hasValidSession: boolean;
}

export interface SessionStorageActions {
  persistSession: (session: CreateSessionResponse) => void;
  setSession: (session: CreateSessionResponse) => void;
  updateFromResume: (session: ResumeSessionResponse) => void;
  clearSessionData: () => void;
  clearRouteData: () => void;
  setCurrentStep: (step: string | null) => void;
  setHasValidSession: (valid: boolean) => void;
  isStoredSessionFresh: () => boolean;
}

export const useSessionStorage = (): SessionStorageState & SessionStorageActions => {
  const [storedSessionId, setStoredSessionId] = useLocalStorage<string | null>(CPQ_SESSION_ID_KEY, null);
  const [storedResumeToken, setStoredResumeToken] = useLocalStorage<string | null>(CPQ_RESUME_TOKEN_KEY, null);
  const [storedAt, setStoredAt] = useLocalStorage<number | null>(CPQ_SESSION_STORED_AT_KEY, null);
  const [currentStep, setCurrentStep] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [hasValidSession, setHasValidSession] = useState(false);

  const clearSessionData = useCallback(() => {
    setStoredSessionId(null);
    setStoredResumeToken(null);
    setStoredAt(null);
    setCurrentStep(null);
    setExpiresAt(null);
    setHasValidSession(false);
    localStorage.removeItem(CPQ_QUALIFICATION_CACHE_KEY);
    localStorage.removeItem(CPQ_QUALIFICATION_FLOW_KEY);
    localStorage.removeItem(CPQ_CHECKOUT_STATE_KEY);
  }, [setStoredSessionId, setStoredResumeToken, setStoredAt]);

  const clearRouteData = useCallback(() => {
    localStorage.removeItem(CPQ_ORDER_DATA_KEY);
  }, []);

  const persistSession = useCallback(
    (session: CreateSessionResponse) => {
      setStoredSessionId(session.sessionId);
      setStoredResumeToken(session.resumeToken);
      setStoredAt(Date.now());
      setCurrentStep(session.currentStep);
      setExpiresAt(session.expiresAt);
      setHasValidSession(true);
    },
    [setStoredSessionId, setStoredResumeToken, setStoredAt]
  );

  const setSession = useCallback(
    (session: CreateSessionResponse) => {
      if (session.currentStep === 'DONE') {
        clearSessionData();
      } else {
        persistSession(session);
      }
    },
    [clearSessionData, persistSession]
  );

  const updateFromResume = useCallback(
    (session: ResumeSessionResponse) => {
      if (session.currentStep === 'DONE') {
        clearSessionData();
      } else {
        setStoredSessionId(session.sessionId);
        setStoredAt(Date.now());
        setCurrentStep(session.currentStep);
        setExpiresAt(session.ui.expiresAt);
        setHasValidSession(true);
      }
    },
    [setStoredSessionId, setStoredAt, clearSessionData]
  );

  const isStoredSessionFresh = useCallback((): boolean => {
    if (!storedAt) return false;
    return Date.now() - storedAt < SESSION_TTL_MS;
  }, [storedAt]);

  return {
    storedSessionId,
    storedResumeToken,
    currentStep,
    expiresAt,
    hasValidSession,
    persistSession,
    setSession,
    updateFromResume,
    clearSessionData,
    clearRouteData,
    setCurrentStep,
    setHasValidSession,
    isStoredSessionFresh,
  };
};
